/**
 * Typed fetch wrapper. Base URL from the environment (see `.env.example`).
 *
 * Every call goes through `request`, so an error surfaces once, with the
 * server's own message attached. Hard Rule 9 on this side of the wire: nothing
 * swallows a failure and returns an empty object that then renders as zero.
 */

import type {
  AreaSeriesPoint,
  Attenuation,
  Provenance,
  BreachPreview,
  BreachPreviewRequest,
  Dam,
  Health,
  Hydrograph,
  Overlay,
  PointQuery,
  RasterValidation,
  RiverReach,
  Run,
  RunResult,
  Scenario,
  ScenarioCreate,
  SatelliteStatus,
  Settlement,
  StudyArea,
  Timesteps,
  UploadedDEM,
} from './types'

/** In dev the Vite proxy forwards /api to 127.0.0.1:8000, so this default works. */
export const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? '/api'

export class ApiError extends Error {
  readonly status: number
  readonly url: string

  constructor(status: number, url: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.url = url
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`
  const response = await fetch(url, init)

  if (!response.ok) {
    // FastAPI puts the useful text in `detail`; a 422 puts a list of field
    // errors there instead. Both are worth showing verbatim - a bare
    // "Request failed" tells the user nothing they can act on.
    let detail = `HTTP ${response.status}`
    try {
      const body = (await response.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') {
        detail = body.detail
      } else if (Array.isArray(body.detail)) {
        detail = body.detail
          .map((item) => {
            const entry = item as { loc?: unknown[]; msg?: string }
            const field = Array.isArray(entry.loc) ? entry.loc.slice(1).join('.') : ''
            return field ? `${field}: ${entry.msg}` : String(entry.msg)
          })
          .join('; ')
      }
    } catch {
      detail = `${detail} ${response.statusText}`.trim()
    }
    throw new ApiError(response.status, url, detail)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function json(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }
}

export const api = {
  /**
   * Escape hatch for endpoints added after this client was written - the
   * particle, comparison and ingest-slot routes. Typed at the call site rather
   * than here, so a new backend route does not need two files edited to be
   * usable.
   */
  raw: <T = unknown>(path: string) => request<T>(path),

  health: () => request<Health>('/health'),
  studyArea: () => request<StudyArea>('/study-area'),
  dams: () => request<Dam[]>('/dams'),
  dam: (id: number) => request<Dam>(`/dams/${id}`),
  reach: (damId: number) => request<RiverReach>(`/dams/${damId}/reach`),
  settlements: () => request<Settlement[]>('/settlements'),
  provenance: () => request<Provenance>('/provenance'),

  scenarios: () => request<Scenario[]>('/scenarios'),
  scenario: (id: number) => request<Scenario>(`/scenarios/${id}`),
  createScenario: (body: ScenarioCreate) =>
    request<Scenario>('/scenarios', json(body)),
  updateScenario: (id: number, body: Partial<ScenarioCreate>) =>
    request<Scenario>(`/scenarios/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  previewBreach: (body: BreachPreviewRequest) =>
    request<BreachPreview>('/scenarios/preview-breach', json(body)),
  uploadHydrograph: (scenarioId: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<Scenario>(`/scenarios/${scenarioId}/hydrograph`, {
      method: 'POST',
      body: form,
    })
  },

  runs: () => request<Run[]>('/runs'),
  run: (id: string) => request<Run>(`/runs/${id}`),
  createRun: (scenarioId: number, solver = 'quickflood') =>
    request<Run>('/runs', json({ scenario_id: scenarioId, solver })),
  cancelRun: (id: string) => request<void>(`/runs/${id}`, { method: 'DELETE' }),
  /** Persist the model the downstream pages show for a run; null = the run's own result. */
  selectModel: (runId: string, key: string | null) =>
    request<{ run_id: string; selected_model: string | null }>('/compare/models/selection', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId, key }),
    }),
  uploadModelOutput: (runId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<Run>(`/runs/${runId}/upload-model-output`, {
      method: 'POST',
      body: form,
    })
  },

  summary: (runId: string) => request<RunResult>(`/runs/${runId}/summary`),
  timesteps: (runId: string) => request<Timesteps>(`/runs/${runId}/timesteps`),
  overlay: (runId: string, layer: string, t?: number, signal?: AbortSignal) =>
    request<Overlay>(
      `/runs/${runId}/overlay/${layer}${t === undefined ? '' : `?t=${t}`}`,
      signal ? { signal } : undefined,
    ),
  extent: (runId: string, t?: number) =>
    request<GeoJsonFeatureCollection>(
      `/runs/${runId}/extent.geojson${t === undefined ? '' : `?t=${t}`}`,
    ),
  hydrograph: (runId: string) => request<Hydrograph>(`/runs/${runId}/hydrograph`),
  areaSeries: (runId: string) =>
    request<AreaSeriesPoint[]>(`/runs/${runId}/area-series`),
  point: (runId: string, lat: number, lon: number) =>
    request<PointQuery>(`/runs/${runId}/point?lat=${lat}&lon=${lon}`),
  attenuation: (runId: string) =>
    request<Attenuation>(`/runs/${runId}/attenuation`),

  validateRaster: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<RasterValidation>('/data/validate-raster', {
      method: 'POST',
      body: form,
    })
  },
  uploadDem: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<UploadedDEM>('/data/dem', { method: 'POST', body: form })
  },

  /** Part 6.2: the four named DEM checks against one site. Stores nothing. */
  validateDem: <T = unknown>(file: File, siteId: string) => {
    const form = new FormData()
    form.append('file', file)
    return request<T>(`/data/validate-dem?site_id=${encodeURIComponent(siteId)}`, {
      method: 'POST',
      body: form,
    })
  },
  /** Stores the DEM only if every check passes for that site; refused with a 422 otherwise. */
  uploadDemForSite: (file: File, siteId: string) => {
    const form = new FormData()
    form.append('file', file)
    return request<UploadedDEM>(`/data/dem?site_id=${encodeURIComponent(siteId)}`, {
      method: 'POST',
      body: form,
    })
  },

  satelliteStatus: () => request<SatelliteStatus>('/satellite/status'),
}

/** Minimal GeoJSON shapes - enough for what the map consumes, no dependency. */
export interface GeoJsonFeatureCollection {
  type: 'FeatureCollection'
  features: GeoJsonFeature[]
}

export interface GeoJsonFeature {
  type: 'Feature'
  geometry: { type: string; coordinates: unknown } | null
  properties: Record<string, unknown> | null
}

/**
 * Resolve a root-relative path the API returned (an overlay PNG) against
 * whatever origin the API lives on.
 *
 * Same-origin in development, where Vite proxies /api, and in Phase 7, where
 * FastAPI serves the built frontend. Only a deployment that points
 * VITE_API_BASE_URL at another host needs the prefix, and then it needs it.
 */
export function assetUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path
  if (/^https?:\/\//.test(API_BASE)) return new URL(path, API_BASE).toString()
  return path
}

/** Absolute URL for a run-level export, used by ExportBar and Copy API URL. */
export function exportUrl(runId: string, kind: string): string {
  return `${API_BASE}/runs/${runId}/export/${kind}`
}

/** The SSE endpoint for a run's progress. */
export function eventsUrl(runId: string): string {
  return `${API_BASE}/runs/${runId}/events`
}
